import { useState } from 'react';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { LinkLogo } from '@/components/ui/LinkLogo';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck,
  FileSearch,
  GitMerge,
  ArrowRight,
  Award,
  Printer,
  CheckCircle2,
} from 'lucide-react';
import drLarkinHeadshot from '@/assets/dr-larkin-headshot.jpg';

/* ── Mock audit data (client-side only) ── */
const auditData = {
  hospitalName: 'Cincinnati Regional Medical Center',
  casesAudited: 50,
  specialty: 'Orthopedic / Spine',
  totalEarnedRevenue: 4_250_000,
  leakageAmount: 782_000,
  leakagePercent: 18.4,
  projectedAnnualRecovery: 3_128_000,
  pillar1: { label: 'Authorization Gap', percent: 34, detail: 'of cases had the primary CPT authorized but secondary "Subset Bundle" codes were missed—leaving earned revenue un-authorized.' },
  pillar2: { label: 'Documentation Gap', percent: 22, detail: 'of cases contained clinical evidence in the operative note that was never linked to a billable CPT code.' },
  pillar3: { label: 'Reconciliation Gap', percent: 41, detail: 'of cases had the "Triple Match" broken pre-billing—Authorization, Documentation, and Surgeon Confirmation were not aligned.' },
};

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export default function AuditReportPage() {
  const [approved, setApproved] = useState(false);

  return (
    <OpsLayout>
      <div className="w-full max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-16">

        {/* ── Header ── */}
        <header className="flex items-center justify-between mb-10">
          <LinkLogo size="lg" showText showDomain={false} />
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12 rounded-full overflow-hidden border-2 border-primary/30">
              <img src={drLarkinHeadshot} alt="Dr. John J. Larkin" className="w-full h-full object-cover" />
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1.5 justify-end">
                <Award className="h-3.5 w-3.5 text-amber-500" />
                <span className="text-[11px] font-semibold text-amber-600 uppercase tracking-wide">
                  Top Doctor 2025
                </span>
              </div>
              <p className="text-xs text-muted-foreground font-medium">John J. Larkin, M.D.</p>
            </div>
          </div>
        </header>

        <Separator className="mb-10" />

        {/* ── Section 1: Executive Summary ── */}
        <section className="mb-12">
          <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5 text-primary text-[10px] uppercase tracking-widest font-semibold">
            Executive Summary
          </Badge>
          <h1 className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight mb-2">
            Revenue Integrity Diagnostic
          </h1>
          <p className="text-lg text-muted-foreground font-medium mb-8">
            {auditData.hospitalName}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Cases Audited', value: `${auditData.casesAudited}`, sub: auditData.specialty },
              { label: 'Total Earned Revenue', value: fmt(auditData.totalEarnedRevenue), sub: 'Projected' },
              { label: 'Identified Leakage', value: fmt(auditData.leakageAmount), sub: `${auditData.leakagePercent}%`, highlight: true },
              { label: 'Annual Recovery', value: fmt(auditData.projectedAnnualRecovery), sub: 'Projected' },
            ].map((metric) => (
              <Card key={metric.label} className={`border ${metric.highlight ? 'border-primary/30 bg-primary/5' : 'border-border bg-secondary'}`}>
                <CardContent className="p-4">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                    {metric.label}
                  </p>
                  <p className={`text-xl md:text-2xl font-extrabold tracking-tight ${metric.highlight ? 'text-primary' : 'text-foreground'}`}>
                    {metric.value}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{metric.sub}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <Separator className="mb-12" />

        {/* ── Section 2: Three Pillar Gap Analysis ── */}
        <section className="mb-12">
          <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5 text-primary text-[10px] uppercase tracking-widest font-semibold">
            Gap Analysis
          </Badge>
          <h2 className="text-xl md:text-2xl font-extrabold text-foreground tracking-tight mb-6">
            The Three Pillar Gap Analysis
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: ShieldCheck, pillar: 'Authorization', ...auditData.pillar1 },
              { icon: FileSearch, pillar: 'Documentation', ...auditData.pillar2 },
              { icon: GitMerge, pillar: 'Reconciliation', ...auditData.pillar3 },
            ].map((p) => (
              <Card key={p.pillar} className="border-border bg-card">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/5 border border-primary/20">
                      <p.icon className="h-4.5 w-4.5 text-primary" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                        Pillar
                      </p>
                      <p className="text-sm font-bold text-foreground">{p.pillar}</p>
                    </div>
                  </div>
                  <div className="flex items-end gap-1">
                    <span className="text-3xl font-extrabold text-primary tracking-tight">
                      {p.percent}%
                    </span>
                    <span className="text-xs text-muted-foreground mb-1 font-medium">{p.label}</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-700"
                      style={{ width: `${p.percent}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {p.detail}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <Separator className="mb-12" />

        {/* ── Section 3: Larkin Standard Validation ── */}
        <section className="mb-12">
          <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5 text-primary text-[10px] uppercase tracking-widest font-semibold">
            Clinical Validation
          </Badge>
          <h2 className="text-xl md:text-2xl font-extrabold text-foreground tracking-tight mb-6">
            The "Larkin Standard" Validation
          </h2>

          <Card className="border-border bg-secondary">
            <CardContent className="p-6 md:p-8">
              <blockquote className="text-base md:text-lg text-foreground/80 leading-relaxed italic border-l-4 border-primary pl-5 mb-6">
                "Our audit of these 50 cases confirms that your surgeons are performing
                high-complexity work that is currently being lost to administrative 'Gray
                Zones.' LINK restores that value."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-primary/30">
                  <img src={drLarkinHeadshot} alt="Dr. John J. Larkin" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-sm font-bold text-foreground">John J. Larkin, M.D.</p>
                  <p className="text-xs text-muted-foreground">Founder & Chief Medical Officer</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <Separator className="mb-12" />

        {/* ── Section 4: Actionable Next Step ── */}
        <section className="mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5 text-primary text-[10px] uppercase tracking-widest font-semibold">
            Next Steps
          </Badge>
          <h2 className="text-xl md:text-2xl font-extrabold text-foreground tracking-tight mb-3">
            Implementation & Audit Credit
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-2xl">
            By moving to a 12-month Service Agreement, the <strong className="text-foreground">$7,500 Audit Fee</strong> is
            fully credited toward Phase 1 Implementation.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            {!approved ? (
              <Button
                onClick={() => setApproved(true)}
                className="bg-cta hover:bg-cta/90 text-cta-foreground gap-2 px-6"
              >
                <ArrowRight className="h-4 w-4" />
                Approve Enterprise Rollout
              </Button>
            ) : (
              <Button disabled className="bg-primary/10 text-primary gap-2 px-6 cursor-default">
                <CheckCircle2 className="h-4 w-4" />
                Rollout Approved
              </Button>
            )}
            <Button
              variant="outline"
              className="gap-2 print:hidden"
              onClick={() => window.print()}
            >
              <Printer className="h-4 w-4" />
              Print Report
            </Button>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="border-t border-border pt-6 pb-4 text-center">
          <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-medium">
            Confidential Procedure Revenue Integrity Diagnostic — Proprietary & Trade Secret
          </p>
        </footer>
      </div>
    </OpsLayout>
  );
}
